import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  ButtonGroup,
  Button,
  Slider,
  Stack,
  InputAdornment,
} from '@mui/material';
import { useWizard } from '../../context/WizardContext';
import ConditionalRender from '../common/ConditionalRender';
import CurrencyInput from '../common/CurrencyInput';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const SocialSecurityStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const { socialSecurity, employment, personal, uiState } = wizardData;
  
  // Initialize progress tracking
  const { trackFieldChange } = useProgressTracking({
    stepName: 'Social Security & Pensions',
    stepNumber: 3
  });
  
  // State for validation messages
  const [ssValidationMessages, setSsValidationMessages] = React.useState({
    client1Current: '',
    client1Projected: '',
    client2Current: '',
    client2Projected: ''
  });

  const handlePensionStartAgeChange = (event: Event, newValue: number | number[]) => {
    updateWizardData({
      employment: {
        ...employment,
        client1: {
          ...employment.client1,
          pensionStartAge: newValue as number,
        },
      },
    });
  };

  const handlePensionStartAgeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWizardData({
      employment: {
        ...employment,
        client1: {
          ...employment.client1,
          pensionStartAge: event.target.value === '' ? 67 : Number(event.target.value),
        },
      },
    });
  };

  const handlePartnerPensionStartAgeChange = (event: Event, newValue: number | number[]) => {
    updateWizardData({
      employment: {
        ...employment,
        client2: {
          ...employment.client2,
          pensionStartAge: newValue as number,
        },
      },
    });
  };

  const handlePartnerPensionStartAgeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWizardData({
      employment: {
        ...employment,
        client2: {
          ...employment.client2,
          pensionStartAge: event.target.value === '' ? 67 : Number(event.target.value),
        },
      },
    });
  };

  // Set default pension start age if not set
  React.useEffect(() => {
    if (employment.client1.hasPension === true && !employment.client1.pensionStartAge) {
      updateWizardData({
        employment: {
          ...employment,
          client1: {
            ...employment.client1,
            pensionStartAge: 67,
          },
        },
      });
    }
    
    if (personal.hasPartner &&
        employment.client2.hasPension === true &&
        !employment.client2.pensionStartAge) {
      updateWizardData({
        employment: {
          ...employment,
          client2: {
            ...employment.client2,
            pensionStartAge: 67,
          },
        },
      });
    }
  }, [employment.client1.hasPension, employment.client2.hasPension]);

  return (
    <Box sx={{ flexGrow: 1 }}>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Social Security Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Social Security Benefits
          </Typography>
          <Typography variant="body1" paragraph>
            {personal.client1.firstName ? `${personal.client1.firstName}, are` : 'Are'} you currently claiming Social Security benefits?
          </Typography>
          <ButtonGroup variant="contained" sx={{ mt: 2 }}>
            <Button
              onClick={() => {
                updateWizardData({
                  socialSecurity: {
                    ...socialSecurity,
                    client1: {
                      ...socialSecurity.client1,
                      currentAmount: socialSecurity.client1.currentAmount,
                    },
                  },
                  uiState: {
                    ...uiState,
                    hasAnsweredSocialSecurityQuestion: true,
                    isClaimingSocialSecurity: true,
                  }
                });
              }}
              color={uiState.hasAnsweredSocialSecurityQuestion && uiState.isClaimingSocialSecurity ? 'primary' : 'inherit'}
              sx={{
                minWidth: '120px',
                backgroundColor: uiState.hasAnsweredSocialSecurityQuestion && uiState.isClaimingSocialSecurity ? undefined : 'grey.300',
                '&:hover': {
                  backgroundColor: uiState.hasAnsweredSocialSecurityQuestion && uiState.isClaimingSocialSecurity ? undefined : 'grey.400',
                }
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                updateWizardData({
                  socialSecurity: {
                    ...socialSecurity,
                    client1: {
                      ...socialSecurity.client1,
                      currentAmount: 0,
                    },
                  },
                  uiState: {
                    ...uiState,
                    hasAnsweredSocialSecurityQuestion: true,
                    isClaimingSocialSecurity: false,
                  }
                });
              }}
              color={uiState.hasAnsweredSocialSecurityQuestion && !uiState.isClaimingSocialSecurity ? 'primary' : 'inherit'}
              sx={{
                minWidth: '120px',
                backgroundColor: uiState.hasAnsweredSocialSecurityQuestion && !uiState.isClaimingSocialSecurity ? undefined : 'grey.300',
                '&:hover': {
                  backgroundColor: uiState.hasAnsweredSocialSecurityQuestion && !uiState.isClaimingSocialSecurity ? undefined : 'grey.400',
                }
              }}
            >
              No
            </Button>
          </ButtonGroup>

          <ConditionalRender show={uiState.hasAnsweredSocialSecurityQuestion && !!uiState.isClaimingSocialSecurity}>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" paragraph>
                What is your estimated annual benefit amount?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                  <CurrencyInput
                    fullWidth
                    label="Annual Benefit Amount"
                    placeholder="26,000"
                    value={socialSecurity.client1.currentAmount || ''}
                    onChange={(value) => {
                      updateWizardData({
                        socialSecurity: {
                          ...socialSecurity,
                          client1: {
                            ...socialSecurity.client1,
                            currentAmount: value,
                          },
                        },
                      });
                      
                      // Validate the amount
                      if (value > 80000) {
                        setSsValidationMessages(prev => ({
                          ...prev,
                          client1Current: 'Please check the annual social security benefit number to ensure accuracy. Benefits rarely exceed $60,000.'
                        }));
                      } else if (value > 0 && value <= 4200) {
                        setSsValidationMessages(prev => ({
                          ...prev,
                          client1Current: 'Please check to make sure you are entering an annual social security amount, not a monthly amount.'
                        }));
                      } else {
                        setSsValidationMessages(prev => ({
                          ...prev,
                          client1Current: ''
                        }));
                      }
                    }}
                    error={!!ssValidationMessages.client1Current}
                    helperText={ssValidationMessages.client1Current}
                  />
                </Box>
              </Box>
            </Box>
          </ConditionalRender>

          <ConditionalRender show={uiState.hasAnsweredSocialSecurityQuestion && (socialSecurity.client1.currentAmount ?? 0) === 0}>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" paragraph>
                What is your estimated annual Social Security benefit at your full retirement age (FRA)?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                  <CurrencyInput
                    fullWidth
                    label="Estimated Annual Future Benefit"
                    placeholder="26,000"
                    value={socialSecurity.client1.projectedAmount !== undefined ? socialSecurity.client1.projectedAmount : ''}
                    onChange={(value) => {
                      updateWizardData({
                        socialSecurity: {
                          ...socialSecurity,
                          client1: {
                            ...socialSecurity.client1,
                            projectedAmount: value,
                          },
                        },
                      });
                      
                      // Validate the amount (allow $0)
                      if (value > 80000) {
                        setSsValidationMessages(prev => ({
                          ...prev,
                          client1Projected: 'Please check the annual social security benefit number to ensure accuracy. Benefits rarely exceed $60,000.'
                        }));
                      } else if (value > 0 && value <= 4200) {
                        setSsValidationMessages(prev => ({
                          ...prev,
                          client1Projected: 'Please check to make sure you are entering an annual social security amount, not a monthly amount.'
                        }));
                      } else {
                        // Clear validation message (allows $0)
                        setSsValidationMessages(prev => ({
                          ...prev,
                          client1Projected: ''
                        }));
                      }
                    }}
                    error={!!ssValidationMessages.client1Projected}
                    helperText={ssValidationMessages.client1Projected}
                  />
                </Box>
              </Box>
            </Box>
          </ConditionalRender>
        </Box>

        {/* Partner Social Security Section */}
        {personal.hasPartner && uiState.hasAnsweredSocialSecurityQuestion && (
          <Box>
            <Typography variant="body1" paragraph>
              {personal.client2.firstName ? `Is ${personal.client2.firstName}` : 'Is your partner'} currently claiming Social Security benefits?
            </Typography>
            <ButtonGroup variant="contained" sx={{ mt: 2 }}>
              <Button
                onClick={() => {
                  updateWizardData({
                    socialSecurity: {
                      ...socialSecurity,
                      partnerIsClaiming: true,
                    },
                    uiState: {
                      ...uiState,
                      hasAnsweredPartnerSocialSecurityQuestion: true,
                    }
                  });
                }}
                color={socialSecurity.partnerIsClaiming === true ? 'primary' : 'inherit'}
                sx={{
                  minWidth: '120px',
                  backgroundColor: !uiState.hasAnsweredPartnerSocialSecurityQuestion || socialSecurity.partnerIsClaiming === true ?
                                  (uiState.hasAnsweredPartnerSocialSecurityQuestion ? undefined : 'grey.300') :
                                  'grey.300',
                  '&:hover': {
                    backgroundColor: socialSecurity.partnerIsClaiming === true ? undefined : 'grey.400',
                  },
                  color: !uiState.hasAnsweredPartnerSocialSecurityQuestion || socialSecurity.partnerIsClaiming === true ?
                        (uiState.hasAnsweredPartnerSocialSecurityQuestion && socialSecurity.partnerIsClaiming === true ? 'white' : 'text.primary') :
                        'text.primary'
                }}
              >
                Yes
              </Button>
              <Button
                onClick={() => {
                  updateWizardData({
                    socialSecurity: {
                      ...socialSecurity,
                      partnerIsClaiming: false,
                    },
                    uiState: {
                      ...uiState,
                      hasAnsweredPartnerSocialSecurityQuestion: true,
                    }
                  });
                }}
                color={socialSecurity.partnerIsClaiming === false ? 'primary' : 'inherit'}
                sx={{
                  minWidth: '120px',
                  backgroundColor: !uiState.hasAnsweredPartnerSocialSecurityQuestion || socialSecurity.partnerIsClaiming === false ?
                                  (uiState.hasAnsweredPartnerSocialSecurityQuestion ? undefined : 'grey.300') :
                                  'grey.300',
                  '&:hover': {
                    backgroundColor: socialSecurity.partnerIsClaiming === false ? undefined : 'grey.400',
                  },
                  color: !uiState.hasAnsweredPartnerSocialSecurityQuestion || socialSecurity.partnerIsClaiming === false ?
                        (uiState.hasAnsweredPartnerSocialSecurityQuestion && socialSecurity.partnerIsClaiming === false ? 'white' : 'text.primary') :
                        'text.primary'
                }}
              >
                No
              </Button>
            </ButtonGroup>

            <ConditionalRender show={uiState.hasAnsweredPartnerSocialSecurityQuestion && socialSecurity.partnerIsClaiming === true}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" paragraph>
                  What is {personal.client2.firstName ? `${personal.client2.firstName}'s` : 'your partner\'s'} current annual benefit amount?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                    <CurrencyInput
                      fullWidth
                      label="Partner's Current Annual Benefit"
                      placeholder="26,000"
                      value={socialSecurity.client2.currentAmount || ''}
                      onChange={(value) => {
                        updateWizardData({
                          socialSecurity: {
                            ...socialSecurity,
                            client2: {
                              ...socialSecurity.client2,
                              currentAmount: value,
                            },
                          },
                        });

                        // Validate the amount
                        if (value > 80000) {
                          setSsValidationMessages(prev => ({
                            ...prev,
                            client2Current: 'Please check the annual social security benefit number to ensure accuracy. Benefits rarely exceed $60,000.'
                          }));
                        } else if (value > 0 && value <= 4200) {
                          setSsValidationMessages(prev => ({
                            ...prev,
                            client2Current: 'Please check to make sure you are entering an annual social security amount, not a monthly amount.'
                          }));
                        } else {
                          setSsValidationMessages(prev => ({
                            ...prev,
                            client2Current: ''
                          }));
                        }
                      }}
                      error={!!ssValidationMessages.client2Current}
                      helperText={ssValidationMessages.client2Current}
                    />
                  </Box>
                </Box>
              </Box>
            </ConditionalRender>

            <ConditionalRender show={uiState.hasAnsweredPartnerSocialSecurityQuestion && socialSecurity.partnerIsClaiming === false}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" paragraph>
                  What is {personal.client2.firstName ? `${personal.client2.firstName}'s` : 'your partner\'s'} estimated annual Social Security benefit at full retirement age (FRA)?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                    <CurrencyInput
                      fullWidth
                      label="Partner's Estimated Future Benefit"
                      placeholder="26,000"
                      value={socialSecurity.client2.projectedAmount !== undefined ? socialSecurity.client2.projectedAmount : ''}
                      onChange={(value) => {
                        updateWizardData({
                          socialSecurity: {
                            ...socialSecurity,
                            client2: {
                              ...socialSecurity.client2,
                              projectedAmount: value,
                            },
                          },
                        });

                        // Validate the amount (allow $0)
                        if (value > 80000) {
                          setSsValidationMessages(prev => ({
                            ...prev,
                            client2Projected: 'Please check the annual social security benefit number to ensure accuracy. Benefits rarely exceed $60,000.'
                          }));
                        } else if (value > 0 && value <= 4200) {
                          setSsValidationMessages(prev => ({
                            ...prev,
                            client2Projected: 'Please check to make sure you are entering an annual social security amount, not a monthly amount.'
                          }));
                        } else {
                          setSsValidationMessages(prev => ({
                            ...prev,
                            client2Projected: ''
                          }));
                        }
                      }}
                      error={!!ssValidationMessages.client2Projected}
                      helperText={ssValidationMessages.client2Projected}
                    />
                  </Box>
                </Box>
              </Box>
            </ConditionalRender>
          </Box>
        )}

        {/* Pension Section */}
        <ConditionalRender show={uiState.hasAnsweredSocialSecurityQuestion && (!personal.hasPartner || uiState.hasAnsweredPartnerSocialSecurityQuestion)}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Pension Benefits
            </Typography>
            <Typography variant="body1" paragraph>
              Do you have a pension?
            </Typography>
            <ButtonGroup variant="contained" sx={{ mt: 2 }}>
              <Button
                onClick={() => {
                  updateWizardData({
                    employment: {
                      ...employment,
                      client1: {
                        ...employment.client1,
                        hasPension: true,
                      },
                    },
                  });
                }}
                color={employment.client1.hasPension === true ? 'primary' : 'inherit'}
                sx={{ 
                  minWidth: '120px',
                  backgroundColor: employment.client1.hasPension === true ? undefined : 'grey.300',
                  '&:hover': {
                    backgroundColor: employment.client1.hasPension === true ? undefined : 'grey.400',
                  }
                }}
              >
                Yes
              </Button>
              <Button
                onClick={() => {
                  updateWizardData({
                    employment: {
                      ...employment,
                      client1: {
                        ...employment.client1,
                        hasPension: false,
                        pensionStartAge: undefined,
                        pensionBenefitAmount: undefined,
                      },
                    },
                  });
                }}
                color={employment.client1.hasPension === false ? 'primary' : 'inherit'}
                sx={{ 
                  minWidth: '120px',
                  backgroundColor: employment.client1.hasPension === false ? undefined : 'grey.300',
                  '&:hover': {
                    backgroundColor: employment.client1.hasPension === false ? undefined : 'grey.400',
                  }
                }}
              >
                No
              </Button>
            </ButtonGroup>

            <ConditionalRender show={employment.client1.hasPension === true}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" paragraph>
                  When will your pension benefits begin?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                  <Box sx={{ flex: '1 1 70%', minWidth: '250px' }}>
                    <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        50
                      </Typography>
                      <Slider
                        value={typeof employment.client1.pensionStartAge === 'number' ? employment.client1.pensionStartAge : 67}
                        onChange={handlePensionStartAgeChange}
                        aria-labelledby="pension-start-age-slider"
                        valueLabelDisplay="auto"
                        step={1}
                        marks={[
                          { value: 67, label: '67' }
                        ]}
                        min={50}
                        max={85}
                        sx={{ mx: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        85
                      </Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: '0 0 130px' }}>
                    <TextField
                      value={employment.client1.pensionStartAge || 67}
                      size="medium"
                      onChange={handlePensionStartAgeInputChange}
                      inputProps={{
                        step: 1,
                        min: 50,
                        max: 85,
                        type: 'number',
                        'aria-labelledby': 'pension-start-age-slider',
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">years</InputAdornment>,
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body1" paragraph>
                    What is your expected annual pension benefit?
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                      <CurrencyInput
                        fullWidth
                        label="Annual Pension Benefit"
                        value={employment.client1.pensionBenefitAmount || ''}
                        onChange={(value) => {
                          updateWizardData({
                            employment: {
                              ...employment,
                              client1: {
                                ...employment.client1,
                                pensionBenefitAmount: value,
                              },
                            },
                          });
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </ConditionalRender>
          </Box>
        </ConditionalRender>

        {/* Partner Pension Section */}
        {personal.hasPartner && employment.client1.hasPension !== undefined && (
          <Box>
            <Typography variant="body1" paragraph>
              Does {personal.client2.firstName ? `${personal.client2.firstName}` : 'your partner'} have a pension?
            </Typography>
            <ButtonGroup variant="contained" sx={{ mt: 2 }}>
              <Button
                onClick={() => {
                  updateWizardData({
                    employment: {
                      ...employment,
                      client2: {
                        ...employment.client2,
                        hasPension: true,
                      },
                    },
                  });
                }}
                color={employment.client2.hasPension === true ? 'primary' : 'inherit'}
                sx={{
                  minWidth: '120px',
                  backgroundColor: employment.client2.hasPension === true ? undefined : 'grey.300',
                  '&:hover': {
                    backgroundColor: employment.client2.hasPension === true ? undefined : 'grey.400',
                  }
                }}
              >
                Yes
              </Button>
              <Button
                onClick={() => {
                  updateWizardData({
                    employment: {
                      ...employment,
                      client2: {
                        ...employment.client2,
                        hasPension: false,
                        pensionStartAge: undefined,
                        pensionBenefitAmount: undefined,
                      },
                    },
                  });
                }}
                color={employment.client2.hasPension === false ? 'primary' : 'inherit'}
                sx={{
                  minWidth: '120px',
                  backgroundColor: employment.client2.hasPension === false ? undefined : 'grey.300',
                  '&:hover': {
                    backgroundColor: employment.client2.hasPension === false ? undefined : 'grey.400',
                  }
                }}
              >
                No
              </Button>
            </ButtonGroup>

            <ConditionalRender show={employment.client2.hasPension === true}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" paragraph>
                  When will {personal.client2.firstName ? `${personal.client2.firstName}'s` : 'your partner\'s'} pension benefits begin?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                  <Box sx={{ flex: '1 1 70%', minWidth: '250px' }}>
                    <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        50
                      </Typography>
                      <Slider
                        value={typeof employment.client2.pensionStartAge === 'number' ? employment.client2.pensionStartAge : 67}
                        onChange={handlePartnerPensionStartAgeChange}
                        aria-labelledby="partner-pension-start-age-slider"
                        valueLabelDisplay="auto"
                        step={1}
                        marks={[
                          { value: 67, label: '67' }
                        ]}
                        min={50}
                        max={85}
                        sx={{ mx: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        85
                      </Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: '0 0 130px' }}>
                    <TextField
                      value={employment.client2.pensionStartAge || 67}
                      size="medium"
                      onChange={handlePartnerPensionStartAgeInputChange}
                      inputProps={{
                        step: 1,
                        min: 50,
                        max: 85,
                        type: 'number',
                        'aria-labelledby': 'partner-pension-start-age-slider',
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">years</InputAdornment>,
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body1" paragraph>
                    What is {personal.client2.firstName ? `${personal.client2.firstName}'s` : 'your partner\'s'} expected annual pension benefit?
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                      <CurrencyInput
                        fullWidth
                        label="Partner's Annual Pension Benefit"
                        value={employment.client2.pensionBenefitAmount || ''}
                        onChange={(value) => {
                          updateWizardData({
                            employment: {
                              ...employment,
                              client2: {
                                ...employment.client2,
                                pensionBenefitAmount: value,
                              },
                            },
                          });
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </ConditionalRender>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SocialSecurityStep; 